import { useSyncExternalStore } from "react";
import {
  CONSENT_STORAGE_KEY,
  type ConsentChoices,
  parseConsentRecord,
  serializeConsentRecord,
} from "./consent-record";

export interface ConsentState {
  /** Null = no valid decision yet: nothing optional may run. */
  readonly choices: ConsentChoices | null;
  /** "stored" = restored on load (head script already told GTM); "user" = clicked now. */
  readonly source: "stored" | "user";
  readonly bannerOpen: boolean;
  /** False outside website pages: footer link and banner stay hidden. */
  readonly active: boolean;
}

const INITIAL_STATE: ConsentState = {
  choices: null,
  source: "stored",
  bannerOpen: false,
  active: false,
};

let state: ConsentState = INITIAL_STATE;
const listeners = new Set<() => void>();

function setState(next: Partial<ConsentState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function readStoredRaw(): string | null {
  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Idempotent: Strict Mode double mount reads storage once. */
export function activateConsentManager(): void {
  if (state.active) return;
  const record = parseConsentRecord(readStoredRaw(), Date.now());
  if (record === null) {
    setState({ active: true, choices: null, bannerOpen: true });
    return;
  }
  setState({
    active: true,
    source: "stored",
    choices: {
      statistics: record.statistics,
      google_ads: record.google_ads,
      meta: record.meta,
    },
    bannerOpen: false,
  });
}

export function saveConsentChoices(choices: ConsentChoices): void {
  try {
    window.localStorage.setItem(
      CONSENT_STORAGE_KEY,
      serializeConsentRecord(choices, Date.now()),
    );
  } catch {
    // Private mode / quota: decision holds for this page view only.
  }
  setState({ choices, source: "user", bannerOpen: false });
}

export function openConsentBanner(): void {
  if (state.active) setState({ bannerOpen: true });
}

export function useConsentState(): ConsentState {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => INITIAL_STATE,
  );
}
